import React, { useState, useEffect } from "react";
import ReactDom from "react-dom";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

export default function IncentiveCalculator({
  setOpenModal,
  id,
  onChange,
  evMsrpCost,
  type = "text",
}) {
  // Default state values
  const defaultState = {
    fleet: "0-20",
    revenue: "< $15 million",
    drayage: "Drayage",
    dac: "Yes",
    port: "No",
    ownership: "Private",
    hvip: 120000,
    isef: 120000,
    drayageInc: 60000,
    dacInc: 36000,
    portInc: 0,
    total: 436000,
    adj: 286200,
  };

  // States
  const [state, setState] = useState({ ...defaultState });

  const handleClick = (e) => {
    e.preventDefault();
    onChange(state.adj);
    setOpenModal(false);
  };

  const handleRadioChange = (group, value) => {
    setState((prev) => ({ ...prev, [group]: value }));

    // Temporary values for calculations
    const tempState = { ...state, [group]: value };

    // Fleet size adjustments
    if (tempState.fleet === "50+") {
      setState((prev) => ({
        ...prev,
        hvip: 0,
        isef: 0,
        drayageInc: 0,
        dacInc: 0,
        portInc: 0,
        total: 0,
        adj: 0,
      }));
      return;
    }

    const newHvip = 120000;
    const newIsef =
      tempState.revenue === "< $15 million" &&
      tempState.fleet === "0-20"
        ? 120000
        : 0;

    const newDrayageInc =
      tempState.drayage === "Drayage" && tempState.fleet !== "50+"
        ? tempState.fleet === "0-20" && tempState.revenue === "< $15 million"
          ? 60000
          : 30000
        : 0;

    const newDacInc =
      tempState.dac === "Yes" &&
      tempState.fleet === "0-20" &&
      tempState.revenue === "< $15 million"
        ? 36000
        : 0;

    const newPortInc =
      tempState.port === "Yes" &&
      tempState.drayage === "Drayage" &&
      tempState.fleet !== "50+"
        ? tempState.fleet === "0-20"
          ? 100000
          : 75000
        : 0;

    setState((prev) => ({
      ...prev,
      hvip: newHvip,
      isef: newIsef,
      drayageInc: newDrayageInc,
      dacInc: newDacInc,
      portInc: newPortInc,
    }));
  };

  useEffect(() => {
    const { hvip, isef, drayageInc, dacInc, portInc, ownership } = state;
    const newTotal = hvip + isef + drayageInc + dacInc + portInc;

    const newAdj =
      ownership === "Private"
        ? Math.min(newTotal, 0.9 * evMsrpCost)
        : Math.min(newTotal, evMsrpCost);

    setState((prev) => ({ ...prev, total: newTotal, adj: newAdj }));
  }, [state.hvip, state.isef, state.drayageInc, state.dacInc, state.portInc, state.ownership, evMsrpCost]);

  return ReactDom.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Incentive Calculator</h3>
        </div>

        {/* Body */}
        <div className="flex flex-col lg:flex-row max-h-[calc(90vh-140px)]">
          {/* Left Side - Form Controls */}
          <div className="flex-1 p-6 overflow-y-auto border-r border-gray-200">
            <div className="space-y-6">
              {[
                {
                  legend: "Fleet Size",
                  name: "fleet",
                  options: ["0-20", "21-49", "50+"],
                  description: <span>
                      Small fleets, including companies, non-profits, and independent owner-operators with 20 or fewer trucks and less than $15 million in annual revenue, 
                      qualify for <a href="https://ww2.arb.ca.gov/resources/fact-sheets/innovative-small-e-fleet-pilot-program" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">ISEF funding</a>, 
                      which doubles the standard HVIP incentive. Public agencies operating 20 or fewer trucks are also eligible. Starting January 1, 2025, private fleets with 50 or more vehicles will no longer be eligible to request new vouchers.
                  </span>
                },
                {
                  legend: "Annual Revenue",
                  name: "revenue",
                  options: ["< $15 million", "$15 million+"],
                  description: <span>
                      A private small fleet with an annual revenue under $15 million qualifies for <a href="https://ww2.arb.ca.gov/resources/fact-sheets/innovative-small-e-fleet-pilot-program" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                      ISEF funding</a>, which doubles the standard HVIP incentive amount.
                  </span>
                },
                {
                  legend: "Truck Type",
                  name: "drayage",
                  options: ["Drayage", "Non-Drayage"],
                  description: <span>
                      Early adopters of class 8 drayage trucks qualify for additional funding equal to 25% of the base amount (HVIP + ISEF). 
                      Further details are available in the <a href="https://californiahvip.org/wp-content/uploads/2024/10/FY23-24-HVIP-Implementation-Manual-103124.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                      FY23-24 HVIP Implementation Manual</a>.
                  </span>
                },
                {
                  legend: "Disadvantaged Community",
                  name: "dac",
                  options: ["Yes", "No"],
                  description: <span>
                      Vehicles domiciled in a Disadvantaged Community (DAC) and purchased or leased by small fleets (20 or fewer trucks with less than $15 million in revenue for private fleets)
                      or California Native American tribal governments are eligible for an additional 15% funding boost to the base incentive. Public and nonprofit fleets have no revenue restrictions. 
                      More details on DAC eligibility can be found in the <a href="https://californiahvip.org/wp-content/uploads/2024/10/FY23-24-HVIP-Implementation-Manual-103124.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                      FY23-24 HVIP Implementation Manual</a>.
                  </span>
                },
                {
                  legend: "Port of L.A. and L.B.",
                  name: "port",
                  options: ["Yes", "No"],
                  description: <span>
                      Voucher enhancements funded by the Ports of Los Angeles and Long Beach offer an additional $100,000 per truck for fleets with 20 or fewer trucks 
                      and $75,000 for larger fleets. This enhancement is stackable with drayage voucher funding. Detailed requirements are available on 
                      the <a href="https://californiahvip.org/funding-updates/drayage-truck-funding-from-ports-of-los-angeles-and-long-beach-available-nov-14-to-stack-with-hvip/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">California HVIP website
                      </a>.
                  </span>
                },
                {
                  legend: "Ownership Classification",
                  name: "ownership",
                  options: ["Private", "Public or Non-Profit"],
                  description: <span>
                      Per the <a href="https://californiahvip.org/wp-content/uploads/2024/10/FY23-24-HVIP-Implementation-Manual-103124.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">FY23-24 HVIP Implementation Manual
                      </a>, for privately owned vehicles, the total voucher amount (including the HVIP base incentive, voucher enhancements, 
                      and other eligible public incentives) cannot exceed 90% of the vehicle purchase price, excluding taxes and fees. For publicly owned vehicles, funding may cover up to 100% of the purchase price, excluding taxes and fees.
                      </span>
                },
              ].map(({ legend, name, options, description }) => (
                  <fieldset key={name} className="border border-gray-300 rounded-lg p-4">
                  <legend className="px-2 text-sm font-medium text-gray-900">
                    <span className="flex items-center space-x-2">
                      <span>{legend}</span>
                      <Tippy interactive={true} content={<div className="max-w-sm text-sm">{description}</div>}>
                        <span className="material-symbols-outlined text-gray-500 cursor-help text-lg">help</span>
                      </Tippy>
                    </span>
                  </legend>
                  <div className="space-y-2 mt-2">
                    {options.map((option) => (
                      <div key={option} className="flex items-center">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                            type="radio"
                            name={name}
                            value={option}
                            defaultChecked={state[name] === option}
                            onChange={(e) => handleRadioChange(name, e.target.value)}
                          />
                          <span className="text-sm text-gray-700">{option}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
          </div>

          {/* Right Side - Results */}
          <div className="flex-1 p-6 bg-gray-50 overflow-y-auto">
            <form className="space-y-4">
              {[
                { label: "Standard HVIP", key: "hvip" },
                { label: "Innovative Small e-Fleet", key: "isef" },
                { label: "Drayage Modifier", key: "drayageInc" },
                { label: "DAC Modifier", key: "dacInc" },
                { label: "Port of L.A. and L.B. Voucher", key: "portInc" },
              ].map(({ label, key }) => (
                <div key={key} className="flex flex-col space-y-1">
                  <label className="text-sm font-medium text-gray-700">{label}</label>
                  <input
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    type={type}
                    value={state[key]}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        [key]: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
              ))}

              <div className="border-t border-gray-300 pt-4 mt-6 space-y-4">
                {[
                  { label: "Total Incentives", key: "total" },
                  { label: "Adjusted Incentives", key: "adj" },
                ].map(({ label, key }) => (
                  <div key={key} className="flex flex-col space-y-1">
                    <label className="text-sm font-semibold text-gray-900">{label}</label>
                    <input
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-semibold"
                      type={type}
                      value={state[key]}
                      onChange={(e) =>
                        setState((prev) => ({
                          ...prev,
                          [key]: Number(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button 
            onClick={() => setOpenModal(false)} 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Cancel
          </button>
          <button 
            onClick={handleClick} 
            className="btn-primary"
          >
            Submit
          </button>
        </div>
      </div>
    </div>,
    document.getElementById("portal")
  );
}

